import {
  MenuItem,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2"
import { sortBy, startCase } from "lodash";

import StartEndTime from "~/components/common/forms/StartEndTime";
import AddressForm from "~/components/common/forms/AddressForm";

import { CalendarBlockBlockTypeEnum, CreateCalendarBlockMutationVariables, OrgConfigQuery, useCommandIndexProvidersQuery } from "~/graphql/__generated__/graphql";
import LabeledSelect from "../common/forms/LabeledSelect";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { blockFieldInfo, CalendarBlockFilters } from "@caredna-marlin/utils/calendarBlockHelper";
import LabeledCheckboxSelect from "../common/forms/LabeledCheckboxSelect";
import { filteredProviderVisitTypes } from "~/utils/providers";

type Props<T extends CreateCalendarBlockMutationVariables> = {
  calendarBlock: T,
  onChange: (updatedCalendarBlock: T) => void,
  /**
   * When set, the block belongs to this provider and the provider select is
   * hidden; the times take the full row. Callers that leave it undefined are
   * unchanged.
   */
  lockedProviderId?: number,
};

export default function CalendarBlockEditForm<T extends CreateCalendarBlockMutationVariables>({
  calendarBlock,
  onChange,
  lockedProviderId,
}: Props<T>) {
  const { data } = useCommandIndexProvidersQuery({ variables: { includeShifts: false, start: null, end: null, active: true }});
  const provider = data?.commandIndexProviders?.providers?.find(p => p.id === calendarBlock.providerId);
  const { visitTypes } = useOrgConfig();

  const selectedVisitTypeIds = calendarBlock.filters?.visitTypeIds ?? [];

  const info = blockFieldInfo(calendarBlock.blockType);

  const parsedVisitTypes = Object.values(visitTypes) as Partial<OrgConfigQuery["visitType"][number][]>;

  // filter visit types by provider service lines visit types.
  const filteredVisitTypes = filteredProviderVisitTypes(provider, parsedVisitTypes)?.map((vt) => ({ key: vt.id, label: startCase(vt.name) }));

  return <Grid container spacing={2}>
    <Grid size={12}>
      <TextField
        label="Title"
        size="small"
        fullWidth
        required
        value={calendarBlock.title || ""}
        onChange={e => onChange({ ...calendarBlock, title: e.target.value })}
      />
    </Grid>
    {lockedProviderId === undefined && (
      <Grid size={5}>
        <LabeledSelect
          size="small"
          fullWidth
          required
          label="Provider"
          value={(calendarBlock.providerId || "").toString()}
          onChange={e => onChange({ ...calendarBlock, providerId: parseInt(e.target.value, 10) })}
        >
          {sortBy(data?.commandIndexProviders?.providers || [], "firstName").map(p => <MenuItem key={p.id} value={p.id.toString()}>{p.firstName} {p.lastName}</MenuItem>)}
        </LabeledSelect>
      </Grid>
    )}
    <Grid size={lockedProviderId === undefined ? 7 : 12}>
      <StartEndTime
        mode="datetime"
        startTime={new Date(calendarBlock.startTime)}
        endTime={new Date(calendarBlock.endTime)}
        onChange={(start, end) => onChange({ ...calendarBlock, startTime: start.toISOString(), endTime: end.toISOString() })}
        timezone={provider?.addressTimezone ?? undefined}
      />
    </Grid>
    {/* Address only for Meeting or Facility */}
    {info.showAddress && (
      <Grid size={12}>
        <AddressForm
          sx={{ mt: -3 }}
          address={calendarBlock}
          setAddress={(address) => onChange({ ...calendarBlock, ...address } as T)}
          required={info.requiredAddress}
        />
      </Grid>
    )}

    {/* Visit Types for Facility or Visit type */}
    {info.showVisitType && (
      <Grid size={12} sx={{ pt: 2 }}>
        {filteredVisitTypes?.length ? <LabeledCheckboxSelect
          label="Allowed Visit Types"
          value={selectedVisitTypeIds}
          onChange={(visitTypeIds: number[]) =>
            onChange(
              {
                ...calendarBlock,
                filters: { visitTypeIds },
              } as T
            )
          }
          options={filteredVisitTypes}
          sx={{ width: "90%" }}
          required={info.shouldRequireVisitTypes}
        /> : (
          provider ? 'Provider has no visit types assigned' : null
        )}
      </Grid>
    )}
  </Grid>;
}

export function isCalendarBlockValid(
  b: CreateCalendarBlockMutationVariables | null,
): boolean {

  function isAddressComplete(b: CreateCalendarBlockMutationVariables): boolean {
    const has = (v?: string | null) => !!(v ?? '').toString().trim();
    return has(b.addressStreet) && has(b.addressCity) && has(b.addressState) && has(b.addressZip) || has(b.addressPlaceId);
  }

  function hasVisitTypes(filters?: CalendarBlockFilters): boolean {
    return !!(filters?.visitTypeIds && filters.visitTypeIds.length > 0);
  }

  if (!b) return false;
  if (!b.providerId) return false;
  const start = new Date(b.startTime);
  const end = new Date(b.endTime);
  if (!(+start) || !(+end) || !(end > start)) return false;

  if (CalendarBlockBlockTypeEnum.Facility === b.blockType && !isAddressComplete(b)) return false;
  if (b.blockType === CalendarBlockBlockTypeEnum.VisitType && !hasVisitTypes(b.filters)) return false;

  return true;
}
